import { Pipe, PipeTransform } from '@angular/core';
import { Learning_Type } from '@app/enums/learning-type.enum';
import { User } from '@app/models/user.model';
import { isValidSubscription } from '@app/utilities/valid-subscription.utilities';
import { environment } from '@environments/environment';

@Pipe({
  name: 'courseDetailRoute',
  pure: false,
  standalone: false,
})
export class CourseDetailRoutePipe implements PipeTransform {
  /**
   * Return transformed route to redirect a course to external url or internal routing (default angular routing)
   * based on learning type, user (signed in) or not, and a matching course id that corresponds
   * to User's enrollment course id for course detail.
   * NOTE: Pipe must be impure due to the aynchronous nature of the user service which contains the user subscription and enrollments.
   * @param user - If user is signed in, then the transformed type will return a different display type.
   * @param learningType - learning type to transform to new display type
   * @param slugName - If the course slug name is provided.
   * @param subscriptionId - Subscription id used to check if the user has this subscription
   * @param relatedSubscriptionIds - Related subscriptions used to check if the course can appear in 1 to N subscriptions
   * @param courseId - Course id used to check if the user has this enrollment
   * @param returnUrl - If return url is provided, redirect user after login
   * @param isFreeCourse - Is Free course if the course IsFreeCourse additional field from course is true, else false.
   * @param parentSlugName - If the parentSlugName is provided with the return Url, redirect to the the page provided.
   * @returns - transformed eLearning route to an external url
   */
  public transform(
    user: User,
    learningType: string,
    slugName: string,
    subscriptionId: number,
    relatedSubscriptionIds: number[],
    courseId: number,
    isFreeCourse: boolean,
    returnUrl: string,
    parentSlugName: string,
    courseCode?: string
  ): string {
    const coursePathSegment =
      courseCode && String(courseCode).trim().length > 0 ? courseCode.trim() : slugName;
    const courseRoute = `course/${coursePathSegment}`;

    switch (learningType) {
      case Learning_Type.QUICK_LEARNING:
        if (user) {
          if (user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            const isSubscribed = isValidSubscription(relatedSubscriptionIds, subscriptionId, user);
            if (isSubscribed) {
              if (user.enrollments && user.enrollments.length > 0) {
                const courseEnrollment = user.enrollments.find(
                  (enrollment) => enrollment.id === courseId
                );
                if (courseEnrollment) {
                  return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
                } else {
                  return `${environment.docebo.url}/learn/course/${courseId}`;
                }
              } else {
                return `${environment.docebo.url}/learn/course/${courseId}`;
              }
            } else {
              if (isFreeCourse) {
                return `${environment.docebo.url}/learn/course/${courseId}`;
              } else {
                return parentSlugName;
              }
            }
          } else {
            if (isFreeCourse) {
              return `${environment.docebo.url}/learn/course/${courseId}`;
            } else {
              return parentSlugName;
            }
          }
        } else {
          if (returnUrl) {
            let newReturnUrl = returnUrl;
            if (returnUrl === 'subscription') {
              newReturnUrl = `subscription/${parentSlugName}`;
            } else {
              newReturnUrl = courseRoute;
            }
            return `${newReturnUrl}`;
          } else {
            return courseRoute;
          }
        }
      case Learning_Type.E_LEARNING:
      case Learning_Type.QUICK_LEARNING:
      case Learning_Type.WORKSHOP:
      case Learning_Type.WEBINAR:
      case Learning_Type.SELF_STUDY:
        if (user) {
          if (user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            // NOTE: Check if user has active subscription ids (it can match 1 or N subscription) that matches incoming subscription id, example
            const isSubscribed = isValidSubscription(relatedSubscriptionIds, subscriptionId, user);
            if (isSubscribed) {
              if (user.enrollments && user.enrollments.length > 0) {
                // Check if user has active subscription id that matches incoming subscription id
                const courseEnrollment = user.enrollments.find(
                  (enrollment) => enrollment.id === courseId
                );
                if (courseEnrollment) {
                  // User has enrollment based on the course id (which is part of the subscription)
                  // NOTE: Check enrollment and redirect user to the external route type which is an external url
                  // NOTE: User enrollment url which has the learn/course/{courseId}/{courseSlug}
                  // Ex. /learn/course/13062/genesys-cloud-speech-and-text-analytics-sentiment-analysis-elearning
                  return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
                } else {
                  // NOTE: Path has elearning in lower case not mixed case as Learning_Type.E_LEARNING
                  return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
                }
              } else {
                // NOTE: Path has elearning in lower case not mixed case as Learning_Type.E_LEARNING
                return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
              }
            } else {
              if (isFreeCourse) {
                // NOTE: Path has elearning in lower case not mixed case as Learning_Type.E_LEARNING
                return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
              } else {
                // Current user is not subscribed, so redirect to subscription detail or 'Learn More'
                return parentSlugName;
              }
            }
          } else {
            if (isFreeCourse) {
              // NOTE: Path has elearning in lower case not mixed case as Learning_Type.E_LEARNING
              return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
            } else {
              // Current user is not subscribed, so redirect to subscription detail or 'Learn More'
              return parentSlugName;
            }
          }
        } else {
          // User needs to login on sign in
          // Default to Learn
          if (returnUrl) {
            let newReturnUrl = returnUrl;
            if (returnUrl === 'subscription') {
              newReturnUrl = `subscription/${parentSlugName}`;
            } else {
              newReturnUrl = courseRoute;
            }
            return `${newReturnUrl}`;
          } else {
            return courseRoute;
          }
        }
      case Learning_Type.CERTIFICATION_EXAM:
        if (user) {
          // Check if user has active subscription id that matches incoming subscription id
          // NOTE: Check if user has active subscription ids (it can match 1 or N subscription) that matches incoming subscription id, example
          const isSubscribed = isValidSubscription(relatedSubscriptionIds, subscriptionId, user);
          if (isSubscribed) {
            if (user.enrollments && user.enrollments.length > 0) {
              // Check if user has active subscription id that matches incoming subscription id
              const courseEnrollment = user.enrollments.find(
                (enrollment) => enrollment.id === courseId
              );
              // TODO: this may change.
              if (courseEnrollment) {
                // User has enrollment based on the course id (which is part of the subscription)
                // Certification exams calls a modal and doesn't not route to internal or external.
                // NOTE: Path has elearning in lower case not mixed case as Learning_Type.E_LEARNING
                return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
              } else {
                // User is not allowed to enroll into this course
                // Pre-prend the course to route to properly redirect to course detail (internal route type)
                // return `course/${slugName}`;
                return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
              }
            } else {
              return `${environment.docebo.url}/learn/course/internal/view/elearning/${courseId}/${slugName}`;
            }
          } else {
            // Not subscribed. Request a Quote Voucher should show and popup a modal.
            return courseRoute;
          }
        } else {
          // User is not signed in, return login
          // NOTE: Route to login page if user is signed in, but must re-route back to target parent slug
          // (ex subscription detail page)
          if (returnUrl) {
            let newReturnUrl = returnUrl;
            if (returnUrl === 'subscription') {
              newReturnUrl = `subscription/${parentSlugName}`;
            } else {
              newReturnUrl = courseRoute;
            }
            return `${newReturnUrl}`;
          } else {
            return courseRoute;
          }
        }
      case Learning_Type.INSTRUCTOR_LED:
      case Learning_Type.INSTRUCTOR_LED_IN_PERSON:
      case Learning_Type.INSTRUCTOR_LED_VIRTUAL:
      case Learning_Type.VIRTUAL:
      case Learning_Type.IN_PERSON:
        // Current user is not subscribed, so redirect to subscription detail or 'Learn More'
        return parentSlugName;
      default:
        return parentSlugName;
    }
  }
}
